import { Fragment, useEffect, useRef, useState } from "react";
import { label as tenantLabel } from "../../lib/labels";
import { alpha, Avatar, Btn, Card, Icon, PageState, Pill, Skeleton, T, tx } from "@v0y4ge/jrni-ui";
import { Screen } from "../../shell/chrome";
import { can } from "../../shell/access";
import { useUser, useTenant } from "../../shell/access";
import { useNav } from "../../shell/router";
import { useEvents } from "../events/useEvents";
import { useDashboard } from "./useDashboard";
import { useT } from "../../lib/i18n";
import type { DashboardStat, DashboardAlert } from "@v0y4ge/api-contracts";
import {
  isEventToday, formatToday, formatTodayShort,
  CollapsibleRightRail, LiveOpsCard, PacingSection,
  PerformanceSection, CustomerBehaviorSection, DashSectionWrap,
  MiniCalendar, SectionHeader,
} from "./dashboard-components";
import { formatStatValue, renderStatDelta, renderStatLabel } from "./dashboard-i18n";
import { StatTile } from "../../shell/composites";
import { calendarDateKey, toDate } from "../../lib/datetime";
import type { UIEvent } from "../../lib/events-adapter";

/* ── Helpers ────────────────────────────────────────────────────────── */
function greet(t: ReturnType<typeof useT>): string {
  const h = new Date().getHours();
  if (h < 12) return t("dashboard.root.greetingMorning", "Good morning");
  if (h < 17) return t("dashboard.root.greetingAfternoon", "Good afternoon");
  return t("dashboard.root.greetingEvening", "Good evening");
}

/* JAI-1606 defect 2 — the "Upcoming this week" panel carried no week predicate:
   it took the first four `status === "upcoming"` rows whatever their date, and
   looked right only because the server returns soonest-first. The window is
   seven calendar days counted in the EVENT's own zone — the same basis as
   isEventToday — so an event months out can no longer sit under a "this week"
   heading. An event with no start instant can't be placed in the window, so it
   is not claimed to be in it. */
const UPCOMING_WINDOW_DAYS = 7;
/* The panel is a summary card with a "View all" link, so it stays capped at four
   rows. The week predicate bounds the DATE RANGE; the cap bounds the ROW COUNT.
   They are independent — a tenant with 40 events in the next seven days must not
   render 40 rows here. */
const UPCOMING_ROW_CAP = 4;
function dayOffsetInEventZone(ev: UIEvent): number | null {
  const startsAt = ev.raw?.startsAt;
  /* StudioEventDto.startsAt is a bare z.string(), so "" and arbitrary text both
     validate. calendarDateKey falls an unparseable input back to `new Date()`,
     which would silently key a garbage start to TODAY and place the row inside
     the window — so parse explicitly here and refuse to place what won't parse. */
  const parsed = startsAt ? toDate(startsAt) : null;
  if (!parsed) return null;
  const tz = ev.raw?.timezone || undefined;
  const dayMs = (key: string): number => {
    const [y, mo, d] = key.split("-").map((n) => parseInt(n, 10));
    return [y, mo, d].some((n) => isNaN(n)) ? NaN : Date.UTC(y, mo - 1, d);
  };
  const from = dayMs(calendarDateKey(new Date(), tz));
  const to = dayMs(calendarDateKey(parsed, tz));
  if (isNaN(from) || isNaN(to)) return null;
  return Math.round((to - from) / 86400000);
}
function isUpcomingThisWeek(ev: UIEvent): boolean {
  if (ev.status !== "upcoming") return false;
  const offset = dayOffsetInEventZone(ev);
  return offset !== null && offset >= 0 && offset < UPCOMING_WINDOW_DAYS;
}

function usePinnedSegments(): string[] {
  const [ids] = useState<string[]>(() => { try { return JSON.parse(localStorage.getItem("pinnedSegments") || "[]") as string[]; } catch { return []; } });
  return ids;
}

// Add event — opens the Journey designer on its Event template, exactly as the Events list's
// New event does (CC, 2026-10-03: the create.* wizard this used to open was removed 2026-08-24,
// so the button landed on "This page doesn't exist").
function CreateEventSplitBtn({ label = "Add event", variant = "primary", full = false }: { label?: string; variant?: string; full?: boolean }) {
  const nav = useNav();
  const isSecondary = variant === "secondary";
  return (
    <Btn variant={isSecondary ? "secondary" : "primary"} icon={<Icon.plus size={14} />} style={full ? { width: "100%" } : undefined} onClick={() => nav.go("newjourneys", { new: "event" })}>{label}</Btn>
  );
}

/* ── Layout persistence ──────────────────────────────────────────────── */
const DASH_LAYOUT_KEY = "dashLayoutOrder";
const DASH_SECTIONS_DEFAULT = ["kpi", "liveops", "today", "columns", "pacing", "performance", "behavior"];
function dashSectionLabel(t: ReturnType<typeof useT>, id: string): string {
  const labels: Record<string, string> = {
    kpi: t("dashboard.root.sectionKpi", "Today's metrics"),
    liveops: t("dashboard.root.sectionLiveops", "Live operations"),
    today: t("dashboard.root.sectionToday", "Today's events"),
    columns: t("dashboard.root.sectionColumns", "Upcoming & needs attention"),
    pacing: t("dashboard.root.sectionPacing", "Targets & pacing"),
    performance: t("dashboard.root.sectionPerformance", "Performance"),
    // behavior label resolves through tenantLabel at render (#1)
  };
  return labels[id] || id;
}
function readDashLayout(): string[] {
  try {
    const raw = localStorage.getItem(DASH_LAYOUT_KEY);
    if (!raw) return [...DASH_SECTIONS_DEFAULT];
    const arr = JSON.parse(raw) as string[];
    if (!Array.isArray(arr)) return [...DASH_SECTIONS_DEFAULT];
    const known = arr.filter((id) => DASH_SECTIONS_DEFAULT.includes(id));
    const missing = DASH_SECTIONS_DEFAULT.filter((id) => !known.includes(id));
    return [...known, ...missing];
  } catch { return [...DASH_SECTIONS_DEFAULT]; }
}
function writeDashLayout(order: string[]) {
  try { localStorage.setItem(DASH_LAYOUT_KEY, JSON.stringify(order)); } catch {}
}

/* ── Dashboard ──────────────────────────────────────────────────────── */
export default function Dashboard() {
  const nav = useNav();
  const t = useT(); // i18n pilot surface 2 (kit v4.2): dashboard.root.* keys
  const u = useUser();
  const firstName = (u.name || "Lori").split(" ")[0];
  const tenantName = useTenant()?.name ?? "";
  const { events: EVENTS, loading, error, reload } = useEvents();
  const dashData = useDashboard();

  // Tone → accent color (matches the contract's tone field)
  const toneToAccent = (t: string): string => ({ primary: T.p1, blue: T.tDarkBlue, green: T.green1, orange: T.orange1, red: T.red1 }[t] || T.p1);
  // Server ctaHrefs are real app URLs — they may carry a query string
  // ("/reports?range=today&from=2026/07/14&to=2026/07/14&metric=events/run") and
  // slashed sub-views ("/calendar/week"). Parse pathname → dotted route id and
  // query → nav params. The old bare `.replace(/-/g,".")` mangled any href with
  // a query into a garbage route id → app 404 (2026-07-14 report). Slash-bearing
  // QUERY VALUES (from=2026/07/14, metric=events/run) ride through untouched.
  const hrefToEntry = (href?: string): { route: string; params: Record<string, string> } => {
    const [path = "", query = ""] = String(href || "").split("?");
    const route = path.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean).join(".").replace(/-/g, ".");
    const params: Record<string, string> = {};
    try { new URLSearchParams(query).forEach((v, k) => { params[k] = v; }); } catch { /* malformed query — route still resolves */ }
    // Registered/Checked-in-today tiles target report views that aren't migrated
    // yet (registrations.today / checkins.today → MigrationPlaceholder). Until
    // those land, go where the check-in-rate tile goes — the Reports overview —
    // params intact (operator call, 2026-07-14): a dashboard KPI must never
    // dead-end on a placeholder.
    const UNMIGRATED_REPORT_FALLBACK: Record<string, string> = { "registrations.today": "reports", "checkins.today": "reports" };
    return { route: UNMIGRATED_REPORT_FALLBACK[route] ?? (route || "notifications"), params };
  };
  const goHref = (href?: string) => { const e = hrefToEntry(href); nav.go(e.route, e.params); };

  /* JAI-1606 defect 1 — a failed events read used to be indistinguishable from
     an empty workspace: `error` went unread, both panels filtered an empty array
     and printed their empty-state copy. The two list panels below now branch on
     the failure (the derived panels — LiveOps, pacing, performance, behaviour,
     the mini calendar — keep rendering whatever rows the store holds, which is
     correct only because the store never blanks previously-loaded rows).

     The branch is per panel and takes a ROW-COUNT term, not `error` alone.
     useEvents preserves the last-good list on a non-first-load failure and sets
     `error` alongside it (useEvents.ts:14-16), so a poll failing on a network
     blip must not destroy rows that are already on screen and are still being
     rendered as fact elsewhere. Empty list + error → the destructive failure
     state. Rows present + error → a non-destructive "couldn't refresh" banner
     above the rows.

     A persona that simply lacks `events.read` gets the permission copy; anything
     else — a 503 cold start, a transport failure — gets the error copy. Retry is
     wired in BOTH branches: `can()` reads the current user's permissions with a
     localStorage role store as fallback, and when that is stale in the false
     direction an entitled user would otherwise be shown a permission wall with
     no way out but a page reload. A genuinely denied user's retry just re-fails
     into the same state. */
  const eventsDenied = !!error && !can("events.read");
  const eventsFailureState = (testid: string) => (
    <PageState variant="inline" noun={t("events.sectionState.nounEvents", "events")}
      testid={testid}
      error={error}
      errorTitle={eventsDenied ? t("dashboard.root.eventsDeniedTitle", "You don\u2019t have permission to see events") : undefined}
      errorBody={eventsDenied
        ? t("dashboard.root.eventsDeniedBody", "Your role can\u2019t read this workspace\u2019s events, so this panel has nothing to show. Nothing has been deleted \u2014 ask an administrator for the events read permission.")
        : undefined}
      onRetry={reload}
      retryLabel={t("common.actions.retry", "Retry")} />
  );
  const staleBanner = (testid: string) => (
    <div data-testid={testid} role="status"
      style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", background: T.orange2, borderBottom: `1px solid ${T.border}` }}>
      <span style={{ display: "inline-flex", color: T.orange1, flexShrink: 0 }}><Icon.info size={14} /></span>
      <div style={{ flex: 1, fontSize: 12, color: T.n2, lineHeight: 1.4 }}>{t("dashboard.root.eventsStaleBanner", "Couldn\u2019t refresh events just now \u2014 showing the list as it was last loaded.")}</div>
      <Btn variant="secondary" size="sm" onClick={reload}>{t("common.actions.retry", "Retry")}</Btn>
    </div>
  );

  const todays = EVENTS.filter(isEventToday);
  const upcoming = EVENTS.filter((e) => e.status === "upcoming");
  const upcomingThisWeek = EVENTS.filter(isUpcomingThisWeek);
  const activeEvents = EVENTS.filter((e) => e.status === "live" || e.status === "upcoming");
  const todayReg = todays.reduce((n, e) => n + e.registered, 0);
  const todayChecked = todays.reduce((n, e) => n + e.checked, 0);
  const atCapacity = todays.filter((e) => e.capacity > 0 && e.registered >= e.capacity).length;
  const revenueUsd = Math.round(activeEvents.reduce((n, e) => n + e.registered, 0) * 18);
  const fmtRev = revenueUsd >= 1000 ? `$${(revenueUsd / 1000).toFixed(1)}k` : `$${revenueUsd}`;

  // Attention items — from the contract's getDashboardAlerts. An in-flight read
  // is NOT "all quiet": while the alerts request is open the card says it is
  // still checking, and the reassurance waits until the answer is actually in.
  const alertItems: DashboardAlert[] = dashData.alerts?.alerts ?? [];
  const attentionItems = alertItems.length > 0
    ? alertItems.map((a) => ({ icon: <Icon.info size={14} />, tone: a.tone, title: a.title, body: a.body, cta: a.ctaLabel || t("dashboard.root.openDetails", "Open details →"), go: () => goHref(a.ctaHref) }))
    : dashData.loading
    ? [{ icon: <Icon.info size={14} />, tone: "info", title: t("dashboard.root.checkingYourWorkspace", "Checking your workspace…"), body: t("dashboard.root.checkingForAlerts", "Looking for anything that needs you."), cta: "", go: () => {} }]
    : [{ icon: <Icon.info size={14} />, tone: "info", title: t("dashboard.root.allQuietOnTheEventsFront", "All quiet on the events front"), body: t("dashboard.root.noAlertsToSurfaceRightNow", "No alerts to surface right now."), cta: t("dashboard.root.openNotifications", "Open notifications →"), go: () => nav.go("notifications") }];

  // Layout editor state
  const [editLayout, setEditLayout] = useState(false);
  const [order, setOrder] = useState<string[]>(readDashLayout);
  const [dragId, setDragId] = useState<string | null>(null);
  const wrapRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const orderRef = useRef(order);
  const dragIdRef = useRef(dragId);
  useEffect(() => { orderRef.current = order; }, [order]);
  useEffect(() => { dragIdRef.current = dragId; }, [dragId]);

  const persistOrder = (next: string[]) => { setOrder(next); writeDashLayout(next); };
  const moveSection = (id: string, dir: number) => {
    const idx = order.indexOf(id), ni = idx + dir;
    if (idx < 0 || ni < 0 || ni >= order.length) return;
    const copy = [...order]; copy.splice(idx, 1); copy.splice(ni, 0, id); persistOrder(copy);
  };
  const resetLayout = () => persistOrder([...DASH_SECTIONS_DEFAULT]);

  useEffect(() => {
    if (!dragId) return;
    document.body.style.userSelect = "none";
    const onMove = (e: PointerEvent) => {
      const y = e.clientY, cur = orderRef.current;
      let targetId: string | null = null;
      for (const id of cur) {
        const el = wrapRefs.current[id];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (y >= r.top && y <= r.bottom) { targetId = id; break; }
      }
      const held = dragIdRef.current;
      if (targetId && held && targetId !== held) {
        const ci = cur.indexOf(held), ti = cur.indexOf(targetId);
        if (ci > -1 && ti > -1 && ci !== ti) {
          const copy = [...cur]; copy.splice(ci, 1); copy.splice(ti, 0, held); setOrder(copy);
        }
      }
    };
    const onUp = () => { writeDashLayout(orderRef.current); setDragId(null); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => { document.body.style.userSelect = ""; window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); window.removeEventListener("pointercancel", onUp); };
  }, [dragId]);

  // Section nodes
  const sectionNodes: Record<string, React.ReactNode> = {
    kpi: (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
        {(dashData.summary?.stats ?? ([] as DashboardStat[])).map((stat) => (
          <StatTile key={stat.id} label={renderStatLabel(stat, t)} value={formatStatValue(stat)} delta={renderStatDelta(stat, t)} deltaPos
            accent={toneToAccent(stat.tone)}
            icon={(Icon[stat.icon as keyof typeof Icon] ?? Icon.chart)({ size: 14 })}
            onClick={() => goHref(stat.href)} />
        ))}
      </div>
    ),
    liveops: <LiveOpsCard events={EVENTS} />,
    today: (
      <Card>
        <div style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("dashboard.root.text", "Today's events")}</h3>
            <Pill tone="primary">{formatTodayShort()}</Pill>
          </div>
          <Btn variant="link" onClick={() => nav.go("calendar.week")}>{t("dashboard.root.text2", "View calendar →")}</Btn>
        </div>
        {error && todays.length === 0 ? (
          <div style={{ padding: 16 }}>{eventsFailureState("dashboard-today-events")}</div>
        ) : todays.length === 0 && loading && EVENTS.length === 0 ? (
          /* Skeleton rows until the first read settles; the empty line only after a read returns nothing (walk 5 item 2). */
          <div role="status" aria-busy="true" aria-label={t("common.status.loading", "Loading…")}>
            {[0, 1].map((i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "100px 1fr 1fr 1fr 1fr", gap: 16, padding: 16, borderTop: i ? `1px solid ${T.border}` : "none", alignItems: "center" }}>
                <Skeleton variant="text" width={60} />
                <Skeleton variant="text" width="80%" />
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="50%" />
                <Skeleton height={8} />
              </div>
            ))}
          </div>
        ) : todays.length === 0 ? (
          <div style={{ padding: 28, textAlign: "center" }}>
            <div style={{ fontSize: 13, color: T.soft }}>{t("dashboard.root.text3", "No events scheduled for")}{" "}{formatToday()}.</div>
            <Btn variant="link" onClick={() => nav.go("events")}>{t("dashboard.root.text4", "Browse the full event list →")}</Btn>
          </div>
        ) : (
          <>
          {error ? staleBanner("dashboard-today-events-stale") : null}
          <div style={{ display: "grid", gridTemplateColumns: "100px 1fr 1fr 1fr 1fr", gap: 0 }}>
            {todays.map((ev, i) => (
              <Fragment key={ev.id}>
                <div onClick={() => nav.openEvent(ev)} style={{ padding: 16, borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer" }}>
                  <div style={{ fontSize: 13, color: T.n1, fontWeight: 600 }}>{ev.time.split(" — ")[0]}</div>
                  <div style={{ fontSize: 12, color: T.soft }}>{ev.time.split(" — ")[1]}</div>
                </div>
                <div onClick={() => nav.openEvent(ev)} style={{ padding: 16, borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", gap: 12, alignItems: "center", borderLeft: `3px solid ${ev.hue}`, cursor: "pointer" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: T.n1, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.title}</div>
                    <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{ev.template}</div>
                  </div>
                </div>
                <div onClick={() => nav.openEvent(ev)} style={{ padding: 16, borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.n2, cursor: "pointer" }}>
                  <Icon.loc size={13} />{ev.location}
                </div>
                <div onClick={() => nav.openEvent(ev)} style={{ padding: 16, borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: T.n2, cursor: "pointer" }}>
                  <Avatar size="small" initials={ev.host.split(" ").map((w) => w[0]).join("")} color={ev.hue} />{ev.host}
                </div>
                <div onClick={() => nav.openEvent(ev)} style={{ padding: 16, borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", flexDirection: "column", gap: 4, cursor: "pointer" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span style={{ color: T.soft }}>{ev.checked > 0 ? t("dashboard.root.checkedInCount.icu", "{count} checked in", { count: ev.checked }) : t("dashboard.root.registeredCount.icu", "{count} registered", { count: ev.registered })}</span>
                    <span style={{ color: T.n1, fontWeight: 600 }}>{ev.capacity > 0 ? t("dashboard.root.text30", "{arg1}%", { arg1: Math.round((ev.registered / ev.capacity) * 100) }) : "∞"}</span>
                  </div>
                  <div style={{ height: 4, background: T.n5, borderRadius: 999, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${ev.capacity > 0 ? Math.min(100, (ev.registered / ev.capacity) * 100) : 0}%`, background: ev.status === "live" ? T.green1 : T.p1 }} />
                  </div>
                </div>
              </Fragment>
            ))}
          </div>
          </>
        )}
      </Card>
    ),
    columns: (
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16 }}>
        <Card>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("dashboard.root.text5", "Upcoming this week")}</h3>
            <Btn variant="link" onClick={() => nav.go("events")}>{t("dashboard.root.text6", "See all →")}</Btn>
          </div>
          {error && upcomingThisWeek.length === 0 ? <div style={{ padding: 16 }}>{eventsFailureState("dashboard-upcoming-week")}</div> : (
          <>
          {error ? staleBanner("dashboard-upcoming-week-stale") : null}
          <ul aria-busy={loading && EVENTS.length === 0 ? true : undefined} style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {upcomingThisWeek.slice(0, UPCOMING_ROW_CAP).map((ev, i) => (
              <li key={ev.id} onClick={() => nav.openEvent(ev)} style={{ padding: "12px 16px", display: "grid", gridTemplateColumns: "40px 1fr auto auto", alignItems: "center", gap: 12, borderTop: i ? `1px solid ${T.border}` : "none", cursor: "pointer" }}>
                <Avatar size="medium" initials={ev.title.split(" ").map((w) => w[0]).slice(0, 2).join("")} color={ev.hue} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, color: T.n1, fontWeight: 600 }}>{ev.title}</div>
                  <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{ev.date} · {ev.time} · {ev.host}</div>
                </div>
                <Pill tone={ev.waitlist > 0 ? "warn" : "neutral"}>{ev.registered}/{ev.capacity > 0 ? ev.capacity : "∞"}</Pill>
                <Btn variant="secondary" size="sm" onClick={(e: React.MouseEvent) => { e.stopPropagation(); nav.openEvent(ev); }}>{t("dashboard.root.text7", "Manage")}</Btn>
              </li>
            ))}
            {/* No "+N more" row: the cap's overflow is covered by the header's
                existing "View all" link, so this needs no new catalog key. */}
            {/* Skeleton rows until the first read settles; the empty line only after a read returns nothing (walk 4 item 5). */}
            {upcomingThisWeek.length === 0 && loading && EVENTS.length === 0
              ? [0, 1, 2].map((i) => (
                <li key={i} aria-hidden="true" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderBottom: `1px solid ${T.n6}` }}>
                  <Skeleton width={40} height={40} />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}><Skeleton variant="text" width="55%" /><Skeleton variant="text" width="35%" height={10} /></div>
                  <Skeleton width={56} height={22} />
                </li>
              ))
              : upcomingThisWeek.length === 0 ? <li style={{ padding: "20px 16px", fontSize: 13, color: T.soft }}>{t("dashboard.root.upcomingWeekEmpty", "Nothing scheduled in the next 7 days.")}</li> : null}
          </ul>
          </>
          )}
        </Card>
        <Card>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
            <h3 style={{ ...tx.h3, fontWeight: 600 }}>{t("dashboard.root.text9", "Needs attention")}</h3>
          </div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {attentionItems.map((a, i) => {
              const col = { danger: T.red1, warn: T.orange1, success: T.green1, info: T.blue1 }[a.tone] || T.blue1;
              const bg = { danger: T.red2, warn: T.orange2, success: T.green2, info: T.blue2 }[a.tone] || T.blue2;
              const bgHov = { danger: "#FBDCE4", warn: "#FCF3CF", success: "#D6EEE0", info: "#DCE7FC" }[a.tone] || "#DCE7FC";
              return (
                <li key={i} onClick={a.go} style={{ padding: "12px 16px", display: "flex", gap: 10, alignItems: "flex-start", borderTop: i ? `1px solid ${T.border}` : "none", cursor: a.cta ? "pointer" : "default", background: bg }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = bgHov)} onMouseLeave={(e) => (e.currentTarget.style.background = bg)}>
                  <span style={{ width: 28, height: 28, borderRadius: 6, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", background: col, color: T.white }}>{a.icon}</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, color: T.n1, fontWeight: 600 }}>{a.title}</div>
                    <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{a.body}</div>
                    {a.cta ? <div style={{ fontSize: 12, color: col, fontWeight: 600, marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>{a.cta} <Icon.arrow size={10} /></div> : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
    ),
    pacing: <PacingSection events={EVENTS} />,
    performance: <PerformanceSection events={EVENTS} />,
    behavior: <CustomerBehaviorSection events={EVENTS} />,
  };

  const renderSections = () => order.map((id, i) => {
    const node = sectionNodes[id];
    if (node === undefined) return null;
    if (!editLayout) return <Fragment key={id}>{node}</Fragment>;
    return (
      <DashSectionWrap key={id} id={id} label={id === "behavior" ? t("dashboard.dashboard.tenantlabelCustomerTCommonCustomerCustomerBehavior", "{value0} behavior", { value0: tenantLabel("customer", t("common.customer", "Customer")) }) : dashSectionLabel(t, id)} index={i} total={order.length}
        isDragging={dragId === id} innerRef={(el) => { wrapRefs.current[id] = el; }}
        onHandleDown={(sid) => setDragId(sid)} onMove={moveSection}>
        {node || <div style={{ padding: "18px 16px", fontSize: 12, color: T.soft }}>{t("dashboard.root.text10", "This section only appears when there's a live event in progress.")}</div>}
      </DashSectionWrap>
    );
  });

  return (
    <Screen rail="dashboard">
      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <div style={{ flex: 1, overflow: "auto", padding: "20px 24px 28px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Greeting */}
          <Card style={{ padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <h1 style={{ ...tx.h2, fontWeight: 700 }}>{greet(t)}, {firstName}</h1>
              <p style={{ ...tx.help, margin: "4px 0 0" }}>
                {/* JAI-1606: a failed read must not state "0 events today" as fact in
                    the most prominent place on the screen. The whole "You have …"
                    construction — lead-in, counts and all — is replaced by one
                    self-contained sentence; the panels below carry the explanation
                    and the retry.

                    Qualified by the same row-count term as the panels: a failed
                    poll that PRESERVED the last-good list leaves `todays` populated
                    and those rows are still rendered as fact below, so the count
                    stays true and the panel's "couldn't refresh" banner carries the
                    failure. Only a genuinely empty failed read suppresses it. */}
                {tenantName ? t("dashboard.root.text31", "{tenantName} ·", { tenantName }) : ""}
                {error && todays.length === 0 ? (
                  <strong style={{ color: T.n1 }}>{t("dashboard.root.todaysCountUnavailable", "Today\u2019s events couldn\u2019t be read.")}</strong>
                ) : (
                  <>{t("dashboard.root.text11", "You have")}{" "}
                  <strong style={{ color: T.n1 }}>{loading ? "…" : t("dashboard.root.eventsTodayCount.icu", "{count, plural, one {# event} other {# events}} today", { count: todays.length })}</strong>,{" "}
                  {/* Same guard as the count before it, and the tenant's own noun (walk 5 item 2). */}
                  {loading ? "…" : t("dashboard.root.customersExpectedCount.icu", "{count, plural, one {# expected {one}} other {# expected {other}}}", {
                    count: todayReg,
                    one: tenantLabel("customer", t("common.customer", "Customer")).toLowerCase(),
                    other: tenantLabel("customers", t("common.customers", "Customers")).toLowerCase(),
                  })}
                  {atCapacity > 0 ? <>{t("dashboard.root.text12", ", and")}{" "}<strong style={{ color: T.red1 }}>{t("dashboard.root.eventsAtCapacityCount.icu", "{count, plural, one {# event} other {# events}} at capacity", { count: atCapacity })}</strong></> : null}.</>
                )}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant={editLayout ? "primary" : "secondary"} icon={<Icon.grip size={14} />} onClick={() => setEditLayout((v) => !v)}>{editLayout ? t("dashboard.root.text18", "Done") : t("dashboard.dashboard.editLayout", "Edit layout")}</Btn>
              <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => nav.go("reports.events")}>{t("dashboard.root.text14", "Reporting")}</Btn>
              {can("events.write") ? <CreateEventSplitBtn label={t("dashboard.root.label", "Add event")} /> : null}
            </div>
          </Card>

          {/* Edit layout banner */}
          {editLayout ? (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: T.p4, border: `1px solid ${T.p2}`, borderRadius: 10 }}>
              <span style={{ display: "inline-flex", color: T.p1, flexShrink: 0 }}><Icon.grip size={16} /></span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t("dashboard.root.text15", "Editing layout")}</div>
                <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.4 }}>{t("dashboard.root.text16", "Drag a section by its handle, or use the ↑ ↓ buttons, to rearrange. Your order is saved automatically.")}</div>
              </div>
              <Btn variant="secondary" size="sm" onClick={resetLayout}>{t("dashboard.root.text17", "Reset to default")}</Btn>
              <Btn size="sm" onClick={() => setEditLayout(false)}>{t("dashboard.root.text18", "Done")}</Btn>
            </div>
          ) : null}

          {renderSections()}
        </div>

        {/* Right rail */}
        <CollapsibleRightRail width={300}>
          <MiniCalendar eventDays={todays.map((e) => { try { return new Date(e.raw?.startsAt || "").getDate(); } catch { return 0; } }).filter(Boolean)} />
          <div>
            <div style={{ ...tx.label, marginBottom: 10 }}>{t("dashboard.root.text19", "Quick add")}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {can("events.write") ? <CreateEventSplitBtn variant="secondary" full label={t("dashboard.root.label2", "New event")} /> : null}
              {can("guests.write") ? <Btn variant="secondary" full icon={<Icon.users size={14} />} onClick={() => nav.go("guests.import")}>{t("dashboard.root.text20", "Import guests")}</Btn> : null}
              {can("campaigns.write") ? <Btn variant="secondary" full icon={<Icon.send size={14} />} onClick={() => nav.go("campaigns.email")}>{t("dashboard.root.text21", "New campaign")}</Btn> : null}
              {!can("events.write") && !can("guests.write") && !can("campaigns.write")
                ? <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.5, padding: "6px 0" }}>{t("dashboard.root.text22", "Viewer role is read-only. Reach out to an Admin to make changes.")}</div>
                : null}
            </div>
          </div>
        </CollapsibleRightRail>
      </div>
    </Screen>
  );
}
